import React, { useMemo } from 'react';

import type { Capability } from '@rtkwlf/capability';
import type { Capabilities, SubscriptionDetail } from '@rtkwlf/rendall';
import { Navigate, Outlet } from 'react-router-dom';

import { hasRequiredCapability } from '@apis/capability/has-required-capability/has-required-capability';
import { useCapabilities } from '@apis/capability/queries/use-capabilities';
import { useLegacyCapabilities } from '@apis/rendall/queries/use-capabilities';
import { useSubscriptions } from '@apis/rendall/queries/use-subscriptions';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';

/**
 *
 * @param capabilities the requires from the capabilities, support keyA or keyB, and will replace the user and subscriptions
 *
 * @returns
 */

export const ProtectedRoute: React.FC<{
  legacyCapabilities?: Partial<Capabilities>;
  subscriptions?: Partial<SubscriptionDetail>;
  redirectPath?: string;
  children?: React.ReactElement;
  capabilityIds?: Capability['id'][];
}> = ({
  children,
  redirectPath = '/403',
  legacyCapabilities: legacyCapRequires,
  subscriptions: subscriptionsRequires,
  capabilityIds: capabilityRequires,
}) => {
  const { data: legacyCapabilities, isFetched: legacyCapabilitiesIsFetched } =
    useLegacyCapabilities();
  const { data: subscriptions, isFetched: subscriptionsIsFetched } =
    useSubscriptions();
  const { data: capabilities, isFetched: isCapabilitiesFetched } =
    useCapabilities();

  const hasAccess = useMemo(() => {
    if (
      subscriptions &&
      subscriptionsRequires &&
      hasAnyRequiredProperty(subscriptionsRequires, subscriptions)
    ) {
      return true;
    }

    const hasCapability = capabilityRequires?.some((requiredCap) =>
      hasRequiredCapability(requiredCap, capabilities, false),
    );
    if (hasCapability) {
      return true;
    }

    if (
      legacyCapabilities &&
      legacyCapRequires &&
      hasAnyRequiredProperty(legacyCapRequires, legacyCapabilities)
    ) {
      return true;
    }

    return false;
  }, [
    subscriptions,
    subscriptionsRequires,
    capabilityRequires,
    legacyCapabilities,
    legacyCapRequires,
    capabilities,
  ]);

  if (
    !hasAccess &&
    legacyCapabilitiesIsFetched &&
    subscriptionsIsFetched &&
    isCapabilitiesFetched
  ) {
    return <Navigate to={redirectPath} replace />;
  }

  return children || <Outlet />;
};
